import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

import { GapDraftReviewStatus } from '@momentum/types';
import type { GapDraftReviewConfidence } from '@momentum/types';

import { InspectorRail } from '@/features/gap-review/components/InspectorRail';
import type { InspectorTab } from '@/features/gap-review/components/InspectorRail';
import { QueueSidebar } from '@/features/gap-review/components/QueueSidebar';
import { QuoteCommentMenu } from '@/features/gap-review/components/QuoteCommentMenu';
import { QuoteCommentTooltip } from '@/features/gap-review/components/QuoteCommentTooltip';
import { ReaderActionBar } from '@/features/gap-review/components/ReaderActionBar';
import { ReaderChrome } from '@/features/gap-review/components/ReaderChrome';
import { ReaderDoc } from '@/features/gap-review/components/ReaderDoc';
import {
  ReaderEditBar,
  ReaderEditForm,
} from '@/features/gap-review/components/ReaderEditForm';
import { RunReportDoc } from '@/features/gap-review/components/RunReportDoc';
import { useGapReviewData } from '@/features/gap-review/gap-review-context';
import { useDraftEditForm } from '@/features/gap-review/hooks/useDraftEditForm';
import { useGapReviewKeyboard } from '@/features/gap-review/hooks/useGapReviewKeyboard';
import { usePaneWidth } from '@/features/gap-review/hooks/usePaneWidth';
import { useQuoteComments } from '@/features/gap-review/hooks/useQuoteComments';
import { titleOf } from '@/features/gap-review/lib/derive-title';
import { pluralize } from '@/features/gap-review/lib/format';
import {
  QUEUE_WIDTH_BOUNDS,
  RAIL_WIDTH_BOUNDS,
} from '@/features/gap-review/lib/pane-width';
import {
  DEFAULT_READER_VIEW,
  isReaderView,
  otherReaderView,
  type ReaderView,
} from '@/features/gap-review/lib/reader-view';
import {
  countByStatus,
  DROP_STATUS,
  isQueueFilter,
  KEEP_STATUS,
  nextUnreviewedAfter,
  type QueueFilter,
  visibleDrafts as selectVisibleDrafts,
} from '@/features/gap-review/lib/queue';
import {
  REVIEW_CONFIDENCE_LEVELS,
  reviewConfidenceOf,
} from '@/features/gap-review/lib/review-confidence';
import {
  useAddGapDraftComment,
  useSetGapDraftReview,
} from '@/shared/hooks/useGapReview';

/** Exported so `routeTree.gen.ts` can name it under declaration emit. */
export interface GapReviewSearch {
  filter: QueueFilter;
  draft?: string;
  view: ReaderView;
}

export const Route = createFileRoute('/_dashboard/gap-review/')({
  component: GapReviewQueue,
  /**
   * The queue's whole position lives in the URL: which verdict filter, which
   * draft, and which of the reader's two documents — plus `execution`, which the
   * layout route declares because every view under it shares that choice. A
   * reviewer who finds something worth a second opinion pastes the address bar
   * into Slack and the other person lands on the same gap, looking at the same
   * layer of it.
   *
   * The selected draft is a search param rather than a child route because the
   * three-column shell IS this view — a `draft/$id` route would have to render the
   * queue from the layout, which also serves the full-page final-review and
   * run-audit views.
   */
  validateSearch: (search: Record<string, unknown>): GapReviewSearch => ({
    filter: isQueueFilter(search.filter) ? search.filter : 'todo',
    draft: typeof search.draft === 'string' ? search.draft : undefined,
    view: isReaderView(search.view) ? search.view : DEFAULT_READER_VIEW,
  }),
});

function GapReviewQueue() {
  const gapReviewData = useGapReviewData();
  const { executionId, drafts: runDrafts } = gapReviewData;
  const {
    execution: urlExecutionId,
    filter,
    draft: draftId,
    view,
  } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  // The layout resolves the run — falling back to the most recent one — and this
  // writes that answer into the address bar. It matters because the queue is what
  // people share: a reader link carries `draft`, and without the run beside it the
  // person opening the link gets whichever run is newest for them, which may not
  // contain that draft at all.
  useEffect(() => {
    if (urlExecutionId !== undefined) return;
    void navigate({
      search: (previous) => ({ ...previous, execution: executionId }),
      replace: true,
    });
  }, [executionId, navigate, urlExecutionId]);

  const [autoAdvance, setAutoAdvance] = useState(true);
  const [tab, setTab] = useState<InspectorTab>('validator');
  /**
   * Held here rather than in the rail so it survives walking the queue: the
   * reviewer who collapsed it wants it to stay collapsed for the next twenty
   * gaps, not just this one.
   */
  const [isRailCollapsed, setIsRailCollapsed] = useState(false);
  const queuePane = usePaneWidth('queue', QUEUE_WIDTH_BOUNDS);
  const railPane = usePaneWidth('inspector', RAIL_WIDTH_BOUNDS);
  const [composeFocusToken, setComposeFocusToken] = useState(0);
  /**
   * Unposted comment text, keyed by draft id. Held here rather than in the Notes
   * tab so it survives switching tabs and walking the queue away and back — the
   * reviewer who typed half a reason should find it when they return.
   */
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>(
    {}
  );
  /**
   * The passage each unposted comment is anchored to, keyed the same way. Two
   * records rather than one object because the text is the half that always
   * exists — a comment about the gap as a whole simply has no entry here.
   */
  const [commentQuotes, setCommentQuotes] = useState<Record<string, string>>(
    {}
  );
  /**
   * The verdict waiting on a confidence level, or null while none is.
   *
   * Pressing Keep or Drop puts the verdict here instead of writing it; the
   * level the reviewer picks next is what files it. Nothing about the draft
   * changes in between, so cancelling leaves no trace.
   */
  const [pendingVerdict, setPendingVerdict] =
    useState<GapDraftReviewStatus | null>(null);

  const setReview = useSetGapDraftReview();
  const addComment = useAddGapDraftComment();

  const queue = useMemo(
    () => selectVisibleDrafts(runDrafts, filter),
    [runDrafts, filter]
  );

  /**
   * The draft the reader shows.
   *
   * A draft named in the URL wins even when the active filter hides it — that is
   * what makes a pasted link work, and what keeps the reader still after a verdict
   * drops the row out of the `todo` list. Otherwise the first row of the queue.
   */
  const currentDraft =
    runDrafts.find((candidate) => candidate._id === draftId) ?? queue.at(0);

  // The picker asks about the gap that was on screen when the verdict key was
  // pressed. Walking the queue with one open has to close it, or the next level
  // pressed would file a verdict on a gap the reviewer never read.
  useEffect(() => {
    setPendingVerdict(null);
  }, [currentDraft?._id]);

  const selectDraft = useCallback(
    (nextDraftId: string | undefined) => {
      void navigate({
        search: (previous) => ({ ...previous, draft: nextDraftId }),
        replace: true,
      });
    },
    [navigate]
  );

  const selectView = useCallback(
    (nextView: ReaderView) => {
      void navigate({
        search: (previous) => ({ ...previous, view: nextView }),
        replace: true,
      });
    },
    [navigate]
  );

  const queueIndex =
    currentDraft === undefined
      ? -1
      : queue.findIndex((candidate) => candidate._id === currentDraft._id);

  const moveBy = useCallback(
    (offset: number) => {
      if (queueIndex === -1) {
        selectDraft(queue.at(0)?._id);
        return;
      }
      const target = queue.at(queueIndex + offset);
      if (target !== undefined) selectDraft(target._id);
    },
    [queue, queueIndex, selectDraft]
  );

  const commentDraft =
    currentDraft === undefined ? '' : commentDrafts[currentDraft._id] ?? '';
  const commentQuote =
    currentDraft === undefined ? undefined : commentQuotes[currentDraft._id];
  const hasCommentDraft = commentDraft.trim().length > 0;

  /**
   * The level the picker opens on: the draft's own once it has one, and the
   * default otherwise. Read here rather than in the bar so that reopening the
   * picker on a decided gap starts from the level that was actually filed.
   */
  const confidence = reviewConfidenceOf(currentDraft);

  /**
   * Files a verdict at the level the reviewer just picked.
   *
   * Reached only through the picker — there is no path that writes a verdict
   * without one, which is what makes the field required in practice rather than
   * only in the DTO.
   */
  const fileVerdict = useCallback(
    (
      status: GapDraftReviewStatus,
      reviewConfidence: GapDraftReviewConfidence
    ) => {
      if (currentDraft === undefined) return;

      const draftBeingDecided = currentDraft;
      // Re-filing the verdict a draft already has is a correction to how sure
      // the reviewer was, not a new decision — so the queue stays where it is
      // and the toast says what actually changed.
      const isRefiling = draftBeingDecided.review.status === status;

      setReview.mutate(
        {
          draftId: draftBeingDecided._id,
          status,
          reviewConfidence,
        },
        {
          onError: (error) =>
            toast.error(
              `Could not save the verdict for “${titleOf(draftBeingDecided)}”`,
              { description: error.message }
            ),
        }
      );

      const verdictName = status === KEEP_STATUS ? 'Kept' : 'Dropped';

      if (isRefiling) {
        toast(`${verdictName} at ${reviewConfidence} confidence`, {
          description: titleOf(draftBeingDecided),
        });
        return;
      }

      // An unposted comment holds the queue still: advancing would leave the
      // reviewer typing about a gap they can no longer see.
      const nextDraft =
        !autoAdvance || hasCommentDraft
          ? undefined
          : nextUnreviewedAfter(queue, draftBeingDecided._id);

      // Where the draft went, then which gap it was, then what the queue did —
      // a reviewer who chorded J/1 needs to know whether the reader moved.
      const keptCount =
        countByStatus(runDrafts).kept + (status === KEEP_STATUS ? 1 : 0);
      const aftermath = [
        titleOf(draftBeingDecided),
        nextDraft !== undefined ? 'advanced to the next unreviewed gap' : '',
        hasCommentDraft ? 'auto-advance held, comment unposted' : '',
      ].filter(Boolean);

      toast(
        status === KEEP_STATUS
          ? `Kept at ${reviewConfidence} confidence · now in Final review (${keptCount})`
          : `Dropped at ${reviewConfidence} confidence · kept in the history`,
        {
          description: aftermath.join(' · '),
          action: {
            label: 'Undo',
            // A second write rather than a rollback: the first one already
            // reached the server, so undoing it is its own decision.
            onClick: () =>
              setReview.mutate({
                draftId: draftBeingDecided._id,
                status: null,
              }),
          },
        }
      );

      if (nextDraft !== undefined) selectDraft(nextDraft._id);
    },
    [
      autoAdvance,
      currentDraft,
      hasCommentDraft,
      queue,
      runDrafts,
      selectDraft,
      setReview,
    ]
  );

  /**
   * Pressing Keep or Drop.
   *
   * It no longer writes: it asks how sure the reviewer is, and the level files
   * the verdict. The one exception is pressing the verdict a draft already has,
   * which still clears it — asking a reviewer how sure they are of an undo
   * would be absurd, and this is the only way back that does not depend on a
   * toast still being on screen.
   */
  const applyVerdict = useCallback(
    (status: GapDraftReviewStatus) => {
      if (currentDraft === undefined) return;

      const draftBeingDecided = currentDraft;
      if (draftBeingDecided.review.status !== status) {
        setPendingVerdict(status);
        return;
      }

      setReview.mutate(
        { draftId: draftBeingDecided._id, status: null },
        {
          onError: (error) =>
            toast.error(
              `Could not clear the verdict for “${titleOf(draftBeingDecided)}”`,
              { description: error.message }
            ),
        }
      );
      toast('Verdict cleared — back in the queue', {
        description: titleOf(draftBeingDecided),
      });
    },
    [currentDraft, setReview]
  );

  const pickConfidence = useCallback(
    (reviewConfidence: GapDraftReviewConfidence) => {
      if (pendingVerdict === null) return;
      fileVerdict(pendingVerdict, reviewConfidence);
      setPendingVerdict(null);
    },
    [fileVerdict, pendingVerdict]
  );

  /**
   * The `change` beside a decided verdict. It reopens the picker rather than
   * going through `applyVerdict`, whose repeat-verdict rule would clear the
   * decision instead of re-filing it.
   */
  const changeConfidence = useCallback((verdict: GapDraftReviewStatus) => {
    setPendingVerdict(verdict);
  }, []);

  // Memoised because `save` closes over them and the keyboard hook binds
  // `save` — inline arrows would re-bind the document listener every render.
  const onEditsSaved = useCallback(
    (changeCount: number) =>
      toast('Edits saved', {
        description: `${pluralize(
          changeCount,
          'change'
        )} recorded against what the agents wrote.`,
      }),
    []
  );
  const onEditsFailed = useCallback(
    (message: string) =>
      toast.error('Could not save the edits', { description: message }),
    []
  );

  const editForm = useDraftEditForm(currentDraft, {
    onSaved: onEditsSaved,
    onError: onEditsFailed,
  });

  const openCompose = useCallback(() => {
    // Composing lives in the rail, so C has to bring it back — otherwise the
    // keystroke silently focuses a textarea nobody can see.
    setIsRailCollapsed(false);
    setTab('notes');
    setComposeFocusToken((token) => token + 1);
  }, []);

  const postComment = useCallback(() => {
    if (currentDraft === undefined || !hasCommentDraft) return;

    const draftBeingCommented = currentDraft;
    const quote = commentQuotes[draftBeingCommented._id];
    addComment.mutate(
      {
        draftId: draftBeingCommented._id,
        text: commentDraft.trim(),
        ...(quote !== undefined && { quote }),
      },
      {
        // No success toast: the comment appears in the rail the reviewer is
        // looking at, and its passage lights up in the reader beside it.
        onSuccess: () => {
          setCommentDrafts((drafts) => ({
            ...drafts,
            [draftBeingCommented._id]: '',
          }));
          setCommentQuotes((quotes) => {
            const { [draftBeingCommented._id]: posted, ...rest } = quotes;
            return rest;
          });
          window.getSelection()?.removeAllRanges();
        },
        onError: (error) =>
          toast.error('Could not post the comment', {
            description: error.message,
          }),
      }
    );
  }, [addComment, commentDraft, commentQuotes, currentDraft, hasCommentDraft]);

  /**
   * Comments anchored to a passage of the gap.
   *
   * The key is what is on screen, not which draft is selected: the same draft
   * rendered as the run report is a different document, and the edit form is a
   * set of inputs where a quote neither highlights nor can be selected into a
   * comment. Each of those recomputes the highlights, and one of them clears
   * them.
   *
   * A right-click on a selection offers to comment on it; taking that up lands
   * the passage in the rail's composer and puts the cursor under it. It is the
   * composer this surface already has — one place a comment is written, one ⌘↵
   * that posts it, and the thread it joins visible above it while it is being
   * typed. The passage stays lit in the reader the whole time.
   */
  const quoteComments = useQuoteComments({
    comments: currentDraft?.review.comments ?? [],
    documentKey: editForm.isEditing
      ? 'editing'
      : `${currentDraft?._id ?? 'none'}:${view}`,
    draftQuote: commentQuote,
    onQuoteCaptured: (quote) => {
      if (currentDraft === undefined) return;
      // Whatever has been typed stays: right-clicking a second passage is a
      // reviewer correcting what the sentence is about, not starting over.
      setCommentQuotes((quotes) => ({ ...quotes, [currentDraft._id]: quote }));
      openCompose();
    },
  });

  const clearCommentQuote = useCallback(() => {
    if (currentDraft === undefined) return;
    setCommentQuotes((quotes) => {
      const { [currentDraft._id]: cleared, ...rest } = quotes;
      return rest;
    });
  }, [currentDraft]);

  // Reached two ways, by `P` and by the reader chrome's Preview button, which
  // call this same function: the shortcut is advertised in the topbar legend and
  // on the button itself, and a legend that named a key with nothing to click
  // beside it left the surface's one way out of the queue invisible to anyone
  // reading it rather than reading the legend.
  const openPreview = useCallback(() => {
    if (currentDraft === undefined) return;
    void navigate({
      to: '/gap-review/preview/$draftId',
      params: { draftId: currentDraft._id },
      // The run travels with the link: the preview reads the draft out of the
      // run the surface has loaded, and arriving without one would land it on
      // the latest run, which need not hold this draft.
      search: { from: 'reader' as const, execution: executionId },
    });
  }, [currentDraft, executionId, navigate]);

  // Discard drops the passage with the text: what is being thrown away is the
  // whole unposted comment, and leaving the quote behind would leave the next
  // sentence typed into this box silently anchored to it.
  const discardComment = useCallback(() => {
    if (currentDraft === undefined) return;
    setCommentDrafts((drafts) => ({ ...drafts, [currentDraft._id]: '' }));
    clearCommentQuote();
  }, [clearCommentQuote, currentDraft]);

  useGapReviewKeyboard({
    isEditing: editForm.isEditing,
    isConfirming: pendingVerdict !== null,
    onPrevious: () => moveBy(-1),
    onNext: () => moveBy(1),
    onKeep: () => applyVerdict(KEEP_STATUS),
    onDrop: () => applyVerdict(DROP_STATUS),
    onPickConfidenceLevel: (levelIndex) => {
      const level = REVIEW_CONFIDENCE_LEVELS[levelIndex];
      if (level !== undefined) pickConfidence(level);
    },
    onCancelConfirm: () => setPendingVerdict(null),
    onCompose: openCompose,
    onPostComment: postComment,
    onSaveEdits: editForm.save,
    onPreview: openPreview,
    onToggleView: () => selectView(otherReaderView(view)),
  });

  return (
    <div className="gr-view">
      {/* The dragged widths ride in as custom properties rather than as a
          grid-template-columns override, so the stylesheet keeps owning the
          layout — including the reader's minimum and the breakpoint that
          stacks all three panes, neither of which an inline template would
          survive. An undragged pane sets nothing and falls back in CSS. */}
      <div
        className="gr-shell"
        data-rail={isRailCollapsed ? 'collapsed' : undefined}
        style={
          {
            ...(queuePane.width !== undefined && {
              '--gr-queue-w': `${queuePane.width}px`,
            }),
            ...(railPane.width !== undefined && {
              '--gr-insp-w': `${railPane.width}px`,
            }),
          } as CSSProperties
        }
      >
        <QueueSidebar
          gapReviewData={gapReviewData}
          visibleDrafts={queue}
          filter={filter}
          currentDraftId={currentDraft?._id}
          // The selected draft goes with the old run: it is not in the next
          // one's list, and carrying it over would leave the reader showing a
          // gap the queue beside it no longer contains.
          onExecutionChange={(nextExecutionId) =>
            void navigate({
              search: (previous) => ({
                ...previous,
                execution: nextExecutionId,
                draft: undefined,
              }),
              replace: true,
            })
          }
          onFilterChange={(nextFilter) =>
            void navigate({
              search: (previous) => ({ ...previous, filter: nextFilter }),
              replace: true,
            })
          }
          onSelectDraft={selectDraft}
          onResize={queuePane.setWidth}
          onResetWidth={queuePane.resetWidth}
        />

        {currentDraft === undefined ? (
          <main className="gr-read">
            <div className="gr-empty gr-empty-inset">
              {runDrafts.length === 0 ? (
                <>
                  <b>No drafts</b>This run wrote no gap drafts. Pick another run
                  from the picker on the left.
                </>
              ) : (
                <>
                  <b>Nothing selected</b>No draft of this run matches the
                  verdict filter on the queue.
                </>
              )}
            </div>
          </main>
        ) : (
          <>
            <main className="gr-read">
              <ReaderChrome
                scopeName={currentDraft.scopeName}
                executionId={currentDraft.executionId}
                executionCompletedAt={
                  gapReviewData.executions.find(
                    (candidate) =>
                      candidate.executionId === currentDraft.executionId
                  )?.completedAt
                }
                index={queueIndex}
                queueLength={queue.length}
                autoAdvance={autoAdvance}
                isEditable={editForm.isEditable}
                isEditing={editForm.isEditing}
                view={view}
                onViewChange={selectView}
                onToggleEdit={
                  editForm.isEditing ? editForm.cancel : editForm.start
                }
                onJumpToScope={() =>
                  void navigate({
                    search: (previous) => ({ ...previous, filter: 'all' }),
                    replace: true,
                  })
                }
                onPrevious={() => moveBy(-1)}
                onNext={() => moveBy(1)}
                onToggleAutoAdvance={() => setAutoAdvance((isOn) => !isOn)}
                onPreview={openPreview}
              />

              {/* The form takes the reader's place rather than turning it into
                  inputs, and the save bar takes the verdict bar's: a reviewer
                  mid-rewrite has no verdict to file yet, and the two bars would
                  otherwise compete for the same ⌘↵. */}
              {/* `display: contents` — the wrapper exists to own the ref and
                  the right-click, and the reader's own layout has to reach the
                  document underneath it as if it were not there. */}
              <div
                className="gr-quote-host"
                ref={quoteComments.docRef}
                onContextMenu={quoteComments.onContextMenu}
              >
                {editForm.isEditing ? (
                  <ReaderEditForm draft={currentDraft} form={editForm} />
                ) : view === 'report' ? (
                  <RunReportDoc draft={currentDraft} key={currentDraft._id} />
                ) : (
                  <ReaderDoc draft={currentDraft} key={currentDraft._id} />
                )}
              </div>

              {editForm.isEditing ? (
                <ReaderEditBar form={editForm} />
              ) : (
                <ReaderActionBar
                  isKept={currentDraft.review.status === KEEP_STATUS}
                  isDropped={currentDraft.review.status === DROP_STATUS}
                  commentCount={currentDraft.review.comments.length}
                  hasCommentDraft={hasCommentDraft}
                  pendingVerdict={pendingVerdict}
                  confidence={confidence}
                  recordedConfidence={currentDraft.review.reviewConfidence}
                  onKeep={() => applyVerdict(KEEP_STATUS)}
                  onDrop={() => applyVerdict(DROP_STATUS)}
                  onPickConfidence={pickConfidence}
                  onChangeConfidence={changeConfidence}
                  onCancelConfirm={() => setPendingVerdict(null)}
                  onCompose={openCompose}
                />
              )}
            </main>

            <InspectorRail
              draft={currentDraft}
              tab={tab}
              onTabChange={setTab}
              commentDraft={commentDraft}
              onCommentDraftChange={(text) =>
                setCommentDrafts((drafts) => ({
                  ...drafts,
                  [currentDraft._id]: text,
                }))
              }
              onPostComment={postComment}
              onDiscardComment={discardComment}
              isPostingComment={addComment.isPending}
              composeFocusToken={composeFocusToken}
              matchedQuotes={quoteComments.matchedQuotes}
              onRevealQuote={quoteComments.revealQuote}
              commentQuote={commentQuote}
              onClearCommentQuote={clearCommentQuote}
              isCollapsed={isRailCollapsed}
              onToggleCollapse={() =>
                setIsRailCollapsed((isCollapsed) => !isCollapsed)
              }
              onResize={railPane.setWidth}
              onResetWidth={railPane.resetWidth}
            />

            {/* Both are pinned to the viewport rather than to the reader, so
                they are rendered outside it — a passage near the bottom of a
                scrolled column would otherwise open its menu below the fold. */}
            {quoteComments.pending !== undefined && (
              <QuoteCommentMenu
                pending={quoteComments.pending}
                onAdd={quoteComments.acceptPending}
                onDismiss={quoteComments.dismissPending}
              />
            )}
            {quoteComments.hovered !== undefined && (
              <QuoteCommentTooltip hovered={quoteComments.hovered} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
